/**
 * The shared contract every leaf-handler group in `./` is written against — the encounter
 * domain's event/result types, the `EncounterHandler<T>` shape a handler takes, and the small
 * immutable-update helpers the handlers reach for.
 *
 * Split out of `../reducer`, whose module comment carries the tier's doctrine (loose
 * validation only, each command builds its own inverse, no `ctx.compendium`).
 */

import { CommandError, err, ok, type ApplyResult, type Result } from "@lancer-vtt/shared";
import type { Cell, Combatant, CombatantId, EncounterState, StatusInstance } from "../types";
import type { EncounterCommand, EncounterLeafCommand } from "../commands";

// ── Events ───────────────────────────────────────────────────────────────────

/**
 * What the encounter reducer broadcasts after a command. A single coarse
 * `encounter_changed` — enough for a client to re-render from the (whole) raw
 * state the room broadcasts (whole raw state, LWW). The
 * `combatantId` (when one combatant changed) is a breadcrumb for surgical UI
 * updates later; `null` for whole-encounter changes (turn/round/order).
 */
export type EncounterChangedEvent = {
  type: "encounter_changed";
  /** The combatant that changed, or `null` for encounter-wide changes. */
  combatantId: CombatantId | null;
  /**
   * The route a `move_token` that carried its own `path` walked (`landing-zone-full-automation` Q1),
   * so a remote client animates that route instead of recomputing the engine's. Absent otherwise.
   */
  path?: Cell[];
}

export type EncounterEvent = EncounterChangedEvent;

/** The encounter domain's concrete `ApplyResult`. */
export type EncounterApplyResult = ApplyResult<EncounterState, EncounterCommand, EncounterEvent>;

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Get a combatant or reject with a `CommandError`. */
export function requireCombatant(
  state: EncounterState,
  id: CombatantId,
): Result<Combatant, CommandError> {
  const c = state.combatants[id];
  if (!c) return err(new CommandError(`unknown combatant ${id}`));
  return ok(c);
}

/** Replace a combatant in the encounter, returning new state. */
export function withCombatant(state: EncounterState, combatant: Combatant): EncounterState {
  return {
    ...state,
    combatants: { ...state.combatants, [combatant.id]: combatant },
  };
}

/** Clamp `n` into `[lo, hi]`. */
export function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

/** A `encounter_changed` event for a combatant id (or `null` for encounter-wide). */
export function changed(combatantId: CombatantId | null): EncounterEvent[] {
  return [{ type: "encounter_changed", combatantId }];
}

/**
 * Capture every per-instance facet of a live {@link StatusInstance} onto the `apply_status`
 * command that will restore it. The **one** builder both status-restoring inverses go
 * through — `remove_status`'s own inverse and `apply_status`'s Flying-grounds co-effect — so
 * the two can't drift apart.
 *
 * `name` is the caller's own `status` field and so isn't here; everything else on the instance
 * is captured verbatim. Undefined-valued keys are omitted rather than written as `undefined`,
 * so an undo produces a byte-identical instance rather than one with extra explicit-undefined
 * keys (the equality several undo/redo tests assert).
 */
export function statusRestoreFacets(inst: StatusInstance) {
  return {
    expiry: inst.expiry,
    ...(inst.appliedTurn !== undefined ? { appliedTurn: inst.appliedTurn } : {}),
    ...(inst.partner !== undefined ? { partner: inst.partner } : {}),
    ...(inst.source !== undefined ? { source: inst.source } : {}),
    ...(inst.weapon !== undefined ? { weapon: inst.weapon } : {}),
    ...(inst.cause !== undefined ? { cause: inst.cause } : {}),
    ...(inst.suspended !== undefined ? { suspended: inst.suspended } : {}),
    ...(inst.ruling !== undefined ? { ruling: inst.ruling } : {}),
    ...(inst.endsOn !== undefined ? { endsOn: inst.endsOn } : {}),
  };
}

/** Errors unless `T` is `never` — the carrier for the exhaustiveness pin just below. */
type AssertNever<T extends never> = T;

/**
 * **Compile-time exhaustiveness pin, on the INPUT side** — the command-tier twin of
 * `steps.ts`'s `_EveryStatusFacetCarriedFromInstance`/`_EveryStatusFacetCarriedFromStep`.
 * Every key of {@link StatusInstance} must appear in {@link statusRestoreFacets}'s output
 * (or be `name`, the caller's own `status` field), so adding a facet to `StatusInstance`
 * fails `bun run typecheck` rather than silently vanishing the next time a GM undoes a
 * status removal.
 *
 * Keyed on `keyof StatusInstance` — the SOURCE type — not on the restore builder's output.
 * An output-keyed pin only sees the keys the builder already names, so a new source field
 * leaves the output shape unchanged and the pin stays green: that is exactly how `weapon`,
 * `cause`, `suspended` and `ruling` all got dropped here with a fully green suite.
 * The runtime proof that the carry-through actually works is `./steps.test.ts`.
 */
type _EveryStatusInstanceFacetRestored = AssertNever<
  Exclude<keyof StatusInstance, keyof ReturnType<typeof statusRestoreFacets> | "name">
>;

/**
 * One leaf case of the encounter reducer, lifted to a named function: it receives the state
 * and the command already narrowed to command type `T`, and returns the same
 * `Result<EncounterApplyResult, CommandError>` the case's `return` produced. `./leafReducer`'s
 * switch is the registration table that binds each command type to its handler.
 */
export type EncounterHandler<T extends EncounterLeafCommand["type"]> = (
  state: EncounterState,
  cmd: Extract<EncounterLeafCommand, { type: T }>,
) => Result<EncounterApplyResult, CommandError>;
